// İSG Portal — Kiosk Cihaz Yönetimi (register / list / get / revoke) API çağrıları.
//
// KAYNAK: api-contract.md §5.9 (Kiosk Device Token — Katman 4c). Backend kanonu;
// burada **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve kimlik kuralları
// (Result unwrap `value`; liste → `value` + `total`/`filteredTotal`) doğrudan
// sözleşmeden türetilmiştir. Serileştirme camelCase'dir (§1).
//
// **Yönetim yüzeyi ≠ aktivasyon.** Cihazın kendi token akışı (aktivasyon/refresh) auth
// katmanındadır (`auth/kiosk.ts` — `kioskActivate`); burası admin'in cihazı **kaydettiği,
// listelediği ve iptal ettiği** yönetim yüzeyidir (adminApi/settingsApi ile aynı desen:
// `api/*.ts` → `* as kioskApi`). Tüm uçlar `[ModuleAction("kiosk", …)]` yetkilidir;
// grant'ı olmayan principal → 403 (asıl yetki backend'dedir — sistem bypass'ı YOK).
//
// **Gizli veri sınırı (§5.9):** `activationCode` **yalnız** register yanıtında (§5.9.1)
// plaintext döner (DB'de hash'li). Liste (§5.9.5) / get (§5.9.6) **asla** activationCode /
// device secret / token taşımaz. İstemci activationCode'u **loglamaz, kalıcı state'te
// tutmaz** — gösterildikten sonra temizlenir (tek-sefer; UI sorumluluğu).

import { api } from './client';
import type { Empty, PagedResult } from './envelope';

// ============================================================================
// Ortak liste parametreleri (api-contract §3 — page/pageSize/search)
// ============================================================================

/** Kiosk cihaz listesi istek parametreleri — api-contract §5.9.5 (`page`/`pageSize`/`search`). */
export interface KioskListParams {
  page?: number;
  pageSize?: number;
  /** Cihaz adı (substring) araması. */
  search?: string;
}

/** `KioskListParams`'ı client `searchParams` şekline indirger (boş/undefined atlanır). */
function listParams(params: KioskListParams): Record<string, string | number | undefined> {
  return {
    page: params.page,
    pageSize: params.pageSize,
    search: params.search && params.search.length > 0 ? params.search : undefined,
  };
}

// ============================================================================
// Tipler (api-contract §5.9)
// ============================================================================

/**
 * Kiosk cihaz durumu — api-contract §5.9.5.
 * `Pending` = kaydedildi, henüz aktive edilmedi · `Active` = aktive edildi ·
 * `Disabled` = devre dışı (yeniden aktivasyon gerekir).
 */
export type KioskStatus = 'Pending' | 'Active' | 'Disabled';

/**
 * Kiosk cihaz kayıt isteği — api-contract §5.9.1 (`POST /auth/kiosk/register`).
 * **Firma istekten ALINMAZ (tenant-scoped):** cihaz daima çağıranın firmasına
 * (token `CompanyId`) bağlanır; istek bir `companyId` alanı **taşımaz**. Şube/saha
 * `uuid` ile verilir (uuid-native; sunucuda aynı firma içinde çözülür) ve **bağımsız**
 * opsiyonel işaretçilerdir. `roleUuids` cihaza atanacak (yetki grant girdisi) rollerdir.
 */
export interface KioskRegisterRequest {
  name: string;
  branchUuid?: string | null;
  siteUuid?: string | null;
  roleUuids?: string[];
}

/**
 * Kiosk cihaz kayıt sonucu — api-contract §5.9.1 (`Result<KioskRegisterResult>`).
 * `activationCode` (8 haneli) **yalnız bu yanıtta** plaintext döner; DB'de hash'lidir
 * ve liste/get **asla** taşımaz. Yeni kod için yeniden kayıt gerekir. `branchUuid`/
 * `siteUuid` verilmediyse `null`. Firma (companyId) yanıtta taşınmaz.
 */
export interface KioskRegisterResult {
  deviceUuid: string;
  name: string;
  branchUuid: string | null;
  siteUuid: string | null;
  /** Tek-kullanımlık aktivasyon kodu — **yalnız burada**; loglanmaz, kalıcı tutulmaz. */
  activationCode: string;
  /** Aktivasyon kodunun son geçerlilik anı (UTC ISO 8601 — §4; sunumda yerel). */
  activationCodeExpiresAt: string;
}

/**
 * Kiosk cihaz görünümü — api-contract §5.9.5/§5.9.6 (`KioskDeviceDto`). Liste ve tekil
 * getirme bu shape'i paylaşır. **`activationCode` / device secret / token ASLA dönmez.**
 * `branchUuid`/`siteUuid`/`siteName` cross-context çözülür; çözülemezse `null`.
 * `lastSeenAt` = cihazın en son device-refresh anı (hiç aktive olmadıysa `null`) — token
 * değeri değil, yalnız zaman damgası. `roleUuids` = cihaza atanmış aktif roller.
 */
export interface KioskDeviceDto {
  uuid: string;
  name: string;
  companyId: number;
  branchUuid: string | null;
  siteUuid: string | null;
  siteName: string | null;
  status: KioskStatus;
  /** UTC ISO 8601 (§4); hiç aktive olmadıysa `null`. Sunumda kullanıcı yerel saati. */
  lastSeenAt: string | null;
  /** UTC ISO 8601 (§4). */
  createdAt: string;
  roleUuids: string[];
}

/**
 * Kiosk cihaz iptal isteği — api-contract §5.9.4 (`POST /auth/kiosk/{uuid}/revoke`).
 * `disable: false` → yalnız aktif oturumları toplu iptal eder (`TokenVersion` bump);
 * `disable: true` → cihazı `Disabled` yapar (yeniden aktivasyona kadar token alamaz).
 */
export interface KioskRevokeRequest {
  disable: boolean;
}

// ============================================================================
// Uçlar (api-contract §5.9)
// ============================================================================

/**
 * `POST /auth/kiosk/register` — admin (`[ModuleAction("kiosk","register")]`). Cihazı
 * çağıranın firmasına kaydeder (companyId istekten alınmaz — §5.9.1). Opsiyonel alanlar
 * yalnız dolu olduklarında gönderilir (boş → `undefined`, backend `null` çözer).
 * Başarı: `KioskRegisterResult` (tek-kullanımlık `activationCode` **yalnız burada**).
 * Hatalar: `400 VALIDATION_FAILED` (boş `name`), `404 BRANCH_NOT_FOUND`/`SITE_NOT_FOUND`.
 */
export function register(body: KioskRegisterRequest): Promise<KioskRegisterResult> {
  const roleUuids = body.roleUuids && body.roleUuids.length > 0 ? body.roleUuids : undefined;
  return api.post<KioskRegisterResult>('auth/kiosk/register', {
    json: {
      name: body.name.trim(),
      branchUuid: body.branchUuid && body.branchUuid.length > 0 ? body.branchUuid : undefined,
      siteUuid: body.siteUuid && body.siteUuid.length > 0 ? body.siteUuid : undefined,
      roleUuids,
    },
  });
}

/**
 * `GET /auth/kiosk` — admin (`[ModuleAction("kiosk","list")]`). Firmanın kiosk cihazlarını
 * **tenant-scoped** listeler (yalnız çağıranın firması). Sayfalı + aranabilir (cihaz adı);
 * en yeni kayıt önce. `activationCode`/secret listede **asla** dönmez (§5.9.5).
 */
export function listDevices(params: KioskListParams = {}): Promise<PagedResult<KioskDeviceDto>> {
  return api.list<KioskDeviceDto>('auth/kiosk', { searchParams: listParams(params) });
}

/**
 * `GET /auth/kiosk/{uuid}` — admin (`[ModuleAction("kiosk","get")]`). Tek cihaz (tenant-scoped).
 * Yok / başka firmaya ait → `404 KIOSK_NOT_FOUND`. `activationCode`/secret yine dönmez (§5.9.6).
 */
export function getDevice(uuid: string): Promise<KioskDeviceDto> {
  return api.get<KioskDeviceDto>(`auth/kiosk/${uuid}`);
}

/**
 * `POST /auth/kiosk/{uuid}/revoke` — admin (`[ModuleAction("kiosk","revoke")]`). Cihazın tüm
 * oturumlarını toplu iptal eder (`TokenVersion` bump). `disable: true` → cihaz `Disabled`.
 * Başarı: `Result<Empty>`. `404 KIOSK_NOT_FOUND` → cihaz yok (§5.9.4).
 */
export function revoke(uuid: string, body: KioskRevokeRequest): Promise<Empty> {
  return api.post<Empty>(`auth/kiosk/${uuid}/revoke`, { json: { disable: body.disable } });
}
